import {AfterViewChecked, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {QuotationService} from "../../../services/quotation-service/quotation.service";
import {LeadService} from "../../../services/lead-service/lead.service";
import {GtmService} from "../../../services/gtm-service/gtm.service";
import {BehaviorSubject} from "rxjs";
import {isNullOrUndefined} from "util";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {FieldId} from "../../../model/activation/FieldId";
import {ActivationControllerService} from "../../../api/services/activation-controller.service";
import {VehicleDetailsResponse} from "../../../api/models/vehicle-details-response";
import {VehicleDetailsViewComponentItem} from "../../../api/models/vehicle-details-view-component-item";
import {VehicleSaveRequest} from "../../../api/models/vehicle-save-request";
import {Address} from "../../../api/models/address";
import {VehicleExtras} from "../../../api/models/vehicle-extras";
import {VehicleModifications} from "../../../api/models/vehicle-modifications";
import {VehicleInformation} from "../../../api/models/vehicle-information";
import {Suburb} from "../../../model/Suburb";
import {ErrorMessages} from "../../../model/ErrorMessages";
import {SearchSuburbResponse} from "../../../model/SearchSuburbResponse";
import {EntityInformationList} from "../../../api/models/entity-information-list";
import {ValidationService} from "../../../services/validation-service/validation.service";

declare let jQuery: any;
declare let jcf:any

@Component({
  selector: 'app-vehicle-details',
  templateUrl: './activation-vehicle-details.component.html'
})
export class ActivationVehicleDetailsComponent implements OnInit, AfterViewChecked {
  viewLoading = false;
  view: VehicleDetailsResponse;
  vehicleAndOwnerParticularsForm: FormGroup = new FormGroup({});
  registeredOwnerDetailsForm: FormGroup = new FormGroup({});
  addressDetailsForm: FormGroup = new FormGroup({});
  vehicleModificationForm: FormGroup = new FormGroup({});
  vehicleExtrasForm: FormGroup = new FormGroup({});
  captureVehicleOwnerDetails = false;

  suburbSearchTerm: BehaviorSubject<string> = new BehaviorSubject<string>('');
  suburbList: BehaviorSubject<Suburb[]> = new BehaviorSubject<Suburb[]>([]);
  suburbSearched = false;
  errorMessage = ErrorMessages;
  loadingSuburbs = false;
  nightTimeSuburbDescription = null;
  dayTimeSuburbDescription = null;
  showSpinner = false;

  private _amountRegex = /^[R\s]*[1-9][0-9]*([.][0-9]+)*$/;

  constructor(public router: Router,
              public quotationService: QuotationService,
              public activationControllerService: ActivationControllerService,
              private _validationService: ValidationService,
              private _leadService: LeadService,
              private _gtmService: GtmService,
              private _changeDetector: ChangeDetectorRef) { }

  ngOnInit() {
    this._loadView();
    this.quotationService.scrollToTop();
    this.suburbSearchTerm.pipe(debounceTime(500),
      distinctUntilChanged()).subscribe(term => {
      this.searchSuburb(term);
    });
  }

  ngAfterViewChecked() {
    this._changeDetector.detectChanges();
  }

  showExtraValueField(element: VehicleDetailsViewComponentItem) {
    return this.vehicleExtrasForm.controls[element.type + '_' + element.label + '_' + element.optionId] && this.vehicleExtrasForm.controls[element.type + '_' + element.label + '_' + element.optionId].value.toString() === 'true';
  }

  validateAmount(elementView: VehicleDetailsViewComponentItem) {
    if (this.vehicleExtrasForm.controls[elementView.type + '_' + elementView.label + '_' + elementView.optionId].value.toString() === 'true') {
      this.vehicleExtrasForm.controls[elementView.label + '_amount'].setValidators([Validators.pattern(this._amountRegex), Validators.required]);
      this.vehicleExtrasForm.controls[elementView.type + '_' + elementView.label + '_' + elementView.optionId].updateValueAndValidity();
    } else {
      this.vehicleExtrasForm.controls[elementView.label + '_amount'].clearValidators();
      this.vehicleExtrasForm.controls[elementView.label + '_amount'].updateValueAndValidity();
    }
  }

  private _loadView() {
    this.viewLoading = true;
    this.activationControllerService.activationV1VehicleDetailsGetResponse().subscribe(response => {
      this.viewLoading = false;
      this.view = response.body;
      this.view.views.forEach(accordion => accordion.collapsed = accordion.id !== FieldId.VEHICLE_AND_OWNER_PARTICULARS);
      this._createForms();
    }, error => {
      this.quotationService.error = true;
    })
  }

  private _createForms() {
    this.view.views.forEach(accordion => {
      if (accordion.id === FieldId.VEHICLE_AND_OWNER_PARTICULARS) {
        accordion.views.forEach(field => {
          if (field.id === FieldId.VEHICLE_AND_OWNER_PARTICULARS_REGISTERED_OWNER) {
            field.value = this.quotationService.registeredOwner;
            this.updateFormsBasedOnInputChange('VEHICLE_AND_OWNER_PARTICULARS_REGISTERED_OWNER', field.value);
          }
          this.vehicleAndOwnerParticularsForm.addControl(field.id,
            new FormControl({value: field.value == '-1' ? null : field.value, disabled: field.disabled}, this._validationService.getFieldValidators(field, false)));
        });
      }
      if (accordion.id === FieldId.ADDRESS_DETAILS) {
        accordion.views.forEach(panel => {
          panel.views.forEach(field => {
            this.addressDetailsForm.addControl(field.id,
              new FormControl({value: field.value, disabled: field.disabled}, this._validationService.getFieldValidators(field, false)));
            if (field.id === FieldId.ADDRESS_DETAILS_NIGHT_TIME_SUBURB || field.id === FieldId.ADDRESS_DETAILS_DAY_TIME_SUBURB) {
              this.selectSuburb({suburbCd: field.options[0].id, description: field.options[0].label}, field.id);
            }
          });
        });
      }
      if (accordion.id === FieldId.VEHICLE_MODIFICATIONS) {
        accordion.views.forEach(multiSelectInput => {
          multiSelectInput.views.forEach(field => {
            this.vehicleModificationForm.addControl(field.type+'_'+field.label+'_'+field.optionId, new FormControl({value: field.value, disabled: field.disabled}));
          });
        });
      }
      if (accordion.id === FieldId.VEHICLE_EXTRAS) {
        accordion.views.forEach(multiSelectInput => {
          multiSelectInput.views.forEach(field => {
            let amountValue = 0;
            for (let i=0; i<this.view.policy.extras.length; i++) {
              if (this.view.policy.extras[i].id === field.optionId) {
                amountValue = this.view.policy.extras[i].insuredValue;
                break;
              }
            }
            this.vehicleExtrasForm.addControl(field.type+'_'+field.label+'_'+field.optionId, new FormControl({value: field.value, disabled: field.disabled}));
            this.vehicleExtrasForm.addControl(field.label+'_amount', new FormControl({value: 'R '+amountValue, disabled: false}));
          });
        });
      }
    });
    this._reloadJCF();
  }

  getFormName(accordionPanelId): FormGroup {
    switch (accordionPanelId) {
      case FieldId.VEHICLE_AND_OWNER_PARTICULARS:
        return this.vehicleAndOwnerParticularsForm;
      case FieldId.REGISTERED_OWNER:
        return this.registeredOwnerDetailsForm;
      case FieldId.ADDRESS_DETAILS:
        return this.addressDetailsForm;
      case FieldId.VEHICLE_MODIFICATIONS:
        return this.vehicleModificationForm;
      case FieldId.VEHICLE_EXTRAS:
        return this.vehicleExtrasForm;
    }
  }

  private _reloadJCF() {
    jQuery(function () {
      // @ts-ignore
      jcf.replaceAll();
    });
  }

  expandNextPanel(currentPanel: VehicleDetailsViewComponentItem, field: VehicleDetailsViewComponentItem) {
    this._trimTextValues(currentPanel, field);
  }

  private _trimTextValues(panel, field) {
    if(this.getFormName(panel.id).controls[field.id].value) {
      this.getFormName(panel.id).controls[field.id].setValue(this.getFormName(panel.id).controls[field.id].value.trim());
    }
  }

  showFieldError(form: FormGroup, field) {
    return form.controls[field].invalid;
  }

  showPanelBasedOnShowCondition(accordion: VehicleDetailsViewComponentItem) {
    if (accordion.showCondition) {
      return this.vehicleAndOwnerParticularsForm.controls[accordion.showCondition.id].value === accordion.showCondition.optionId[0];
    }
    return true;
  }

  showFieldBasedOnShowCondition(form: FormGroup, field: VehicleDetailsViewComponentItem) {
    if (field.showCondition) {
      return form.controls[field.showCondition.id].value === field.showCondition.optionId[0];
    }
    return true;
  }

  updateFormsBasedOnInputChange(field, option) {
    if (field === FieldId.VEHICLE_AND_OWNER_PARTICULARS_REGISTERED_OWNER) {
      if (option === '-1') {
        this.captureVehicleOwnerDetails = true;
        this._reloadJCF();
        this.view.views.forEach(accordion => {
          if (accordion.id === FieldId.REGISTERED_OWNER) {
            accordion.views.forEach(field => {
              this.registeredOwnerDetailsForm.addControl(field.id,
                new FormControl({value: field.value == undefined ? null : field.value, disabled: field.disabled}, this._validationService.getFieldValidators(field, false)));
            });
          }
        })
      } else {
        this.captureVehicleOwnerDetails = false;
        for (let controlsKey in this.registeredOwnerDetailsForm.controls) {
          this.registeredOwnerDetailsForm.removeControl(controlsKey);
        }
        this.registeredOwnerDetailsForm.updateValueAndValidity();
      }
    }

    if (field === FieldId.VEHICLE_AND_OWNER_PARTICULARS_VEHICLE_FINANCING) {
      if (option === '27') {
        this.vehicleAndOwnerParticularsForm.controls.VEHICLE_AND_OWNER_PARTICULARS_VEHICLE_FINANCING_OTHER.setValidators(Validators.required);
      } else {
        this.vehicleAndOwnerParticularsForm.controls.VEHICLE_AND_OWNER_PARTICULARS_VEHICLE_FINANCING_OTHER.clearValidators();
        this.vehicleAndOwnerParticularsForm.controls.VEHICLE_AND_OWNER_PARTICULARS_VEHICLE_FINANCING_OTHER.setValue('');
        this.vehicleAndOwnerParticularsForm.controls.VEHICLE_AND_OWNER_PARTICULARS_VEHICLE_FINANCING_OTHER.updateValueAndValidity();
      }
    }

    if (field === FieldId.REGISTERED_OWNER_IDENTIFICATION_TYPE) {
      const registeredOwnerDetails = this.view.views.find(accordion => accordion.id === 'REGISTERED_OWNER');
      const ownerIdField = registeredOwnerDetails.views.find(field => field.id === FieldId.REGISTERED_OWNER_ID_NUMBER);
      const ownerPassportField = registeredOwnerDetails.views.find(field => field.id === FieldId.REGISTERED_OWNER_PASSPORT_NUMBER);
      const ownerDateOfBirthField = registeredOwnerDetails.views.find(field => field.id === FieldId.REGISTERED_OWNER_DATE_OF_BIRTH);
      const ownerGenderField = registeredOwnerDetails.views.find(field => field.id === FieldId.REGISTERED_OWNER_GENDER);

      if (option === '2') {
        this._reloadJCF();
        this.registeredOwnerDetailsForm.controls[ownerDateOfBirthField.id].setValidators(this._validationService.getFieldValidators(ownerDateOfBirthField, true));
        this.registeredOwnerDetailsForm.controls[ownerGenderField.id].setValidators(this._validationService.getFieldValidators(ownerGenderField, true));
        this.registeredOwnerDetailsForm.controls[ownerPassportField.id].setValidators(this._validationService.getFieldValidators(ownerPassportField, true));
        this.registeredOwnerDetailsForm.updateValueAndValidity();
        this.registeredOwnerDetailsForm.controls[ownerIdField.id].clearValidators();
        this.registeredOwnerDetailsForm.controls[ownerIdField.id].setValue(null);
        this.registeredOwnerDetailsForm.controls[ownerIdField.id].updateValueAndValidity();
      } else if (option === '1') {
        this.registeredOwnerDetailsForm.controls[ownerIdField.id].setValidators(this._validationService.getFieldValidators(ownerIdField, true));
        this.registeredOwnerDetailsForm.controls[ownerIdField.id].updateValueAndValidity();
        this.registeredOwnerDetailsForm.controls[ownerPassportField.id].clearValidators();
        this.registeredOwnerDetailsForm.controls[ownerPassportField.id].setValue(null);
        this.registeredOwnerDetailsForm.controls[ownerPassportField.id].updateValueAndValidity();
        this.registeredOwnerDetailsForm.controls[ownerDateOfBirthField.id].clearValidators();
        this.registeredOwnerDetailsForm.controls[ownerDateOfBirthField.id].setValue(null);
        this.registeredOwnerDetailsForm.controls[ownerDateOfBirthField.id].updateValueAndValidity();
        this.registeredOwnerDetailsForm.controls[ownerGenderField.id].clearValidators();
        this.registeredOwnerDetailsForm.controls[ownerGenderField.id].setValue(null);
        this.registeredOwnerDetailsForm.controls[ownerGenderField.id].updateValueAndValidity();
      }
    }

    if (field === FieldId.ADDRESS_DETAILS_NIGHT_TIME_ADDRESS_TYPE) {
      const nightTimeAddressPanel = this.view.views.find(accordion => accordion.id === 'ADDRESS_DETAILS').views.find(panel => panel.id === 'ADDRESS_DETAILS_NIGHT_TIME');
      const nightComplexNameField = nightTimeAddressPanel.views.find(field => field.id === FieldId.ADDRESS_DETAILS_NIGHT_TIME_UNIT_COMPLEX_NAME);
      const nightComplexNumberField = nightTimeAddressPanel.views.find(field => field.id === FieldId.ADDRESS_DETAILS_NIGHT_TIME_UNIT_COMPLEX_NUMBER);

      if (option === '9') {
        this.addressDetailsForm.controls[nightComplexNameField.id].setValidators(this._validationService.getFieldValidators(nightComplexNameField, true));
        this.addressDetailsForm.controls[nightComplexNumberField.id].setValidators(this._validationService.getFieldValidators(nightComplexNumberField, true));
        this.addressDetailsForm.updateValueAndValidity();
      } else {
        this.addressDetailsForm.controls[nightComplexNameField.id].clearValidators();
        this.addressDetailsForm.controls[nightComplexNameField.id].setValue('');
        this.addressDetailsForm.controls[nightComplexNameField.id].updateValueAndValidity();
        this.addressDetailsForm.controls[nightComplexNumberField.id].clearValidators();
        this.addressDetailsForm.controls[nightComplexNumberField.id].setValue('');
        this.addressDetailsForm.controls[nightComplexNumberField.id].updateValueAndValidity();
      }
    }

    if (field === FieldId.ADDRESS_DETAILS_DAY_TIME_ADDRESS_TYPE) {
      const dayTimeAddressPanel = this.view.views.find(accordion => accordion.id === 'ADDRESS_DETAILS').views.find(panel => panel.id === 'ADDRESS_DETAILS_DAY_TIME');
      const dayComplexNameField = dayTimeAddressPanel.views.find(field => field.id === FieldId.ADDRESS_DETAILS_DAY_TIME_UNIT_COMPLEX_NAME);
      const dayComplexNumberField = dayTimeAddressPanel.views.find(field => field.id === FieldId.ADDRESS_DETAILS_DAY_TIME_UNIT_COMPLEX_NUMBER);

      if (option === '9') {
        this.addressDetailsForm.controls[dayComplexNameField.id].setValidators(this._validationService.getFieldValidators(dayComplexNameField, true));
        this.addressDetailsForm.controls[dayComplexNumberField.id].setValidators(this._validationService.getFieldValidators(dayComplexNumberField, true));
        this.addressDetailsForm.updateValueAndValidity();
      } else {
        this.addressDetailsForm.controls[dayComplexNameField.id].clearValidators();
        this.addressDetailsForm.controls[dayComplexNameField.id].setValue('');
        this.addressDetailsForm.controls[dayComplexNameField.id].updateValueAndValidity();
        this.addressDetailsForm.controls[dayComplexNumberField.id].clearValidators();
        this.addressDetailsForm.controls[dayComplexNumberField.id].setValue('');
        this.addressDetailsForm.controls[dayComplexNumberField.id].updateValueAndValidity();
      }
    }
  }

  suburbInputChange(value: any) {
    this.suburbSearchTerm.next(value);
  }

  public searchSuburb(phrase: string): void {
    if (phrase.length >= 4) {
      this.loadingSuburbs = true;
      // clean any special chars from the input
      phrase = phrase.replace(/[^a-zA-Z\-\'\s\,]/g, '');
      this.quotationService.searchSuburb(phrase).subscribe((response: SearchSuburbResponse) => {
        this.suburbSearched = true;
        this.loadingSuburbs = false;
        this.suburbList.next(response.suburbLookupList);
      }, error => {
        this.quotationService.error = true;
        this.loadingSuburbs = false;
      });
    }
  }

  public selectSuburb(suburb: Suburb, field) {
    if (!isNullOrUndefined(suburb)) {
      this.loadingSuburbs = false;
      this.addressDetailsForm.controls[field].setValue(suburb.suburbCd);
      field === FieldId.ADDRESS_DETAILS_NIGHT_TIME_SUBURB ? this.nightTimeSuburbDescription = suburb.description : this.dayTimeSuburbDescription = suburb.description;
    }
    this.suburbList.next([]);
  }

  get enableContinueButton() {
    return this.vehicleAndOwnerParticularsForm.valid && this.addressDetailsForm.valid && this.vehicleModificationForm.valid &&
      this.vehicleExtrasForm.valid && this.registeredOwnerDetailsForm.valid;
  }

  saveAndContinue() {
    if(this.enableContinueButton) {
      this.showSpinner = true;
      this.activationControllerService.activationV1VehicleDetailsPutResponse(this._getSaveDetailsRequest()).subscribe(response => {
        this.showSpinner = false;
        if (response.status === 200 && response.body.status === 'VALID') {
          this._leadService.insureRecommendedQuote = 'Vehicle details';
          this._leadService.updateLead().subscribe();
          this._gtmService.postGtmEvent('form_step_complete', 'submit_car_details', 6);
          this.router.navigateByUrl('activation-cover-details');
        } else {
          this.quotationService.error = true;
        }
      }, error => {
        this.quotationService.error = true;
      });
    }else {
      this.showValidation(this.view.views);
    }

  }

  private _getSaveDetailsRequest(): VehicleSaveRequest {
    const vehicleAndOwnerParticularsMap = new Map();
    Object.keys(this.vehicleAndOwnerParticularsForm.controls).forEach(field => vehicleAndOwnerParticularsMap.set(field, this.vehicleAndOwnerParticularsForm.get(field).value));
    const vehicle: VehicleInformation = {
      colourCode: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_COLOUR),
      engineNumber: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_ENGINE_NUMBER),
      financeHouseCode: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_VEHICLE_FINANCING),
      financeHouseName: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_VEHICLE_FINANCING),
      insuredValueAmount: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_INSURED_AMOUNT),
      insuredValueCode: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_INSURED_TYPE),
      isVehicleNew: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_BRAND_NEW_INDICATOR),
      registrationNumber: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_REGISTRATION_NUMBER),
      usageCode: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_MAIN_USE),
      vinNumber: vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_VIN),
      itemEntityInfoList: this.captureVehicleOwnerDetails ? this._getRegisteredOwnerDetails() : []
    };
    
    this.quotationService.vehicleInfo = vehicle;

    const addressDetailsMap = new Map();
    Object.keys(this.addressDetailsForm.controls).forEach(field => addressDetailsMap.set(field, this.addressDetailsForm.get(field).value));
    const nightTimeAddress: Address = {
      addressCode: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_NIGHT_TIME_ADDRESS_TYPE),
      addressLine1: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_NIGHT_TIME_STREET_NUMBER),
      addressLine2: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_NIGHT_TIME_STREET_NAME),
      addressLine3: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_NIGHT_TIME_UNIT_COMPLEX_NUMBER),
      addressLine4: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_UNIT_COMPLEX_NAME),
      addressSecurityCode: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_NIGHT_TIME_ADDRESS_TYPE),
      suburbCode: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_NIGHT_TIME_SUBURB)
    };
    const dayTimeAddress: Address = {
      addressCode: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_ADDRESS_TYPE),
      addressLine1: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_STREET_NUMBER),
      addressLine2: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_STREET_NAME),
      addressLine3: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_UNIT_COMPLEX_NUMBER),
      addressLine4: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_UNIT_COMPLEX_NAME),
      addressSecurityCode: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_SECURITY),
      suburbCode: addressDetailsMap.get(FieldId.ADDRESS_DETAILS_DAY_TIME_SUBURB)
    };

    const vehicleModificationsMap = new Map();
    Object.keys(this.vehicleModificationForm.controls).forEach(field => vehicleModificationsMap.set(field, this.vehicleModificationForm.get(field).value));
    const modifications: VehicleModifications[] = [];

    vehicleModificationsMap.forEach(function(value, key) {
      let keyArr = key.split('_');
      let vehicleMod: VehicleModifications = {
        modificationCode: keyArr[keyArr.length-1],
        selected: value
      };
      modifications.push(vehicleMod);
    });

    const vehicleExtrasMap = new Map();
    Object.keys(this.vehicleExtrasForm.controls).forEach(field => vehicleExtrasMap.set(field, this.vehicleExtrasForm.get(field).value));
    const extras: VehicleExtras[] = [];

    vehicleExtrasMap.forEach(function(value, key) {
      let keyArr = key.split('_');

      if (!key.toString().includes('amount')) {
        let vehicleMod: VehicleExtras = {
          extrasCode: keyArr[keyArr.length-1],
          selected: value
        };
        if (vehicleExtrasMap.has(keyArr[2]+'_amount')) {
          vehicleMod.insuredValueAmount = vehicleExtrasMap.get(keyArr[2]+'_amount').replace('R ', '');
        }
        extras.push(vehicleMod);
      }
    });

    this.quotationService.registeredOwner = vehicleAndOwnerParticularsMap.get(FieldId.VEHICLE_AND_OWNER_PARTICULARS_REGISTERED_OWNER);

    return new class implements VehicleSaveRequest {
      dayTimeAddress: Address = dayTimeAddress;
      extras: Array<VehicleExtras> = extras;
      modifications: Array<VehicleModifications> = modifications;
      nightTimeAddress: Address = nightTimeAddress;
      vehicle: VehicleInformation = vehicle;
    }
  }

  private _getRegisteredOwnerDetails(): EntityInformationList[] {
    const ownerDetailsMap = new Map();
    Object.keys(this.registeredOwnerDetailsForm.controls).forEach(field => ownerDetailsMap.set(field, this.registeredOwnerDetailsForm.get(field).value));
    const legalRefValue = this.registeredOwnerDetailsForm.controls.REGISTERED_OWNER_IDENTIFICATION_TYPE.value === '1' ? ownerDetailsMap.get(FieldId.REGISTERED_OWNER_ID_NUMBER) : ownerDetailsMap.get(FieldId.REGISTERED_OWNER_PASSPORT_NUMBER);

    return [{
      dateOfBirth: ownerDetailsMap.get(FieldId.REGISTERED_OWNER_DATE_OF_BIRTH) ? ownerDetailsMap.get(FieldId.REGISTERED_OWNER_DATE_OF_BIRTH) + ' 00:00' : null,
      firstName: ownerDetailsMap.get(FieldId.REGISTERED_OWNER_NAME),
      gender: ownerDetailsMap.get(FieldId.REGISTERED_OWNER_GENDER),
      initials: ownerDetailsMap.get(FieldId.REGISTERED_OWNER_INITIALS),
      legalReferenceTypeCode: ownerDetailsMap.get(FieldId.REGISTERED_OWNER_IDENTIFICATION_TYPE),
      legalReferenceValue: legalRefValue,
      surname: ownerDetailsMap.get(FieldId.REGISTERED_OWNER_SURNAME),
      titleCode: ownerDetailsMap.get(FieldId.REGISTERED_OWNER_TITLE),
      roleCode: 3
    }];
  }

  goBack() {
    this._gtmService.postGtmEvent('back_button_clicked', 'car_details_back', 6);
    this.router.navigateByUrl('activation-planholder-details');
  }

  showAccordion(accordion: VehicleDetailsViewComponentItem) {
    accordion.collapsed = !accordion.collapsed;
    let viewsToValidate = this.view.views.slice(0, this.view.views.findIndex(x => x.id === accordion.id));
    this.showValidation(viewsToValidate);
  }

  showValidation(views: VehicleDetailsViewComponentItem[]) {
    views.forEach(x => {
      x.showValidation = true;
      x.collapsed = this.getFormName(x.id).valid;
    });
  }

}

